import { useCallback } from "react";
import { useParams } from "react-router";
import { useGlobalLocalContext } from "src/storage/localStorage";
import type { EnvRouteKey } from "src/utils/appRoutes/appRoutes";
import type {
  ExactPathParam,
  PathParams,
} from "src/utils/appRoutes/appRoutes.types";
import { buildLink } from "src/utils/buildLink/buildLink";

export const useBuildLink = (): (<T extends PathParams | EnvRouteKey>(
  pathParams: T extends PathParams ? ExactPathParam<T> : T
) => string) => {
  // links should NOT be dependent on the "env" in the URL but rather the "env" in the global storage:
  // for example, a person can start with the URL "/users" which do not contain the "env" in the URL,
  // and since the sidebar links are being built using this hook, the links will be built using the "env" in the global storage,
  // which should always exists (see "useEnv" in "Topbar.hooks.ts")
  const { env_name: urlEnv } = useParams();
  const storageEnv = useGlobalLocalContext((state) => state.env);
  const env = urlEnv || storageEnv;

  return useCallback(
    <T extends PathParams | EnvRouteKey>(
      pathParams: T extends PathParams ? ExactPathParam<T> : T
    ) => {
      if (typeof env === "undefined") {
        console.error(`Trying to build link with no :env_name param`);
        return "";
      }

      return buildLink(env, pathParams as any) ?? "";
    },
    [env]
  );
};
